/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0 and the Server Side Public License, v 1; you may not use this file except
 * in compliance with, at your election, the Elastic License 2.0 or the Server
 * Side Public License, v 1.
 */
import React, { cloneElement, Component, Children } from 'react';
import PropTypes from "prop-types";
import classNames from 'classnames';
import { keysOf } from '../../common';
import { get } from '../../../services/objects';
import { EuiFormHelpText } from '../form_help_text';
import { EuiFormErrorText } from '../form_error_text';
import { EuiFormLabel } from '../form_label';
import { htmlIdGenerator } from '../../../services/accessibility';
var displayToClassNameMap = {
  row: null,
  rowCompressed: 'euiFormRow--compressed',
  columnCompressed: 'euiFormRow--compressed euiFormRow--horizontal',
  center: null,
  centerCompressed: 'euiFormRow--compressed',
  columnCompressedSwitch: 'euiFormRow--compressed euiFormRow--horizontal euiFormRow--hasSwitch'
};
export var DISPLAYS = keysOf(displayToClassNameMap);
export class EuiFormRow extends Component {
  static defaultProps = {
    display: 'row',
    hasEmptyLabelSpace: false,
    fullWidth: false,
    describedByIds: [],
    labelType: 'label',
    hasChildLabel: true
  };
  state = {
    isFocused: false,
    id: this.props.id || htmlIdGenerator()()
  };
  onFocus = (...args) => {
    // Doing this to allow onFocus to be called correctly from the child input element as this component overrides it
    const onChildFocus = get(this.props, 'children.props.onFocus');

    if (onChildFocus) {
      onChildFocus(...args);
    }

    this.setState(({
      isFocused
    }) => {
      if (!isFocused) {
        return {
          isFocused: true
        };
      } else {
        return null;
      }
    });
  };
  onBlur = (...args) => {
    // Doing this to allow onBlur to be called correctly from the child input element as this component overrides it
    const onChildBlur = get(this.props, 'children.props.onBlur');

    if (onChildBlur) {
      onChildBlur(...args);
    }

    this.setState({
      isFocused: false
    });
  };

  render() {
    const {
      children,
      helpText,
      isInvalid,
      error,
      label,
      labelType,
      labelAppend,
      hasEmptyLabelSpace,
      fullWidth,
      className,
      describedByIds,
      display,
      hasChildLabel,
      id: propsId,
      isDisabled,
      ...rest
    } = this.props;
    const {
      id
    } = this.state;
    const hasLabel = label || labelAppend;
    const classes = classNames('euiFormRow', {
      'euiFormRow--hasEmptyLabelSpace': hasEmptyLabelSpace,
      'euiFormRow--fullWidth': fullWidth,
      'euiFormRow--hasLabel': hasLabel
    }, displayToClassNameMap[display], // Safe use of ! as default prop is 'row'
    className);
    let optionalHelpTexts;

    if (helpText) {
      const helpTexts = Array.isArray(helpText) ? helpText : [helpText];
      optionalHelpTexts = helpTexts.map((helpText, i) => {
        const key = typeof helpText === 'string' ? helpText : i;
        return <EuiFormHelpText key={key} id={`${id}-help-${i}`} className="euiFormRow__text">
            {helpText}
          </EuiFormHelpText>;
      });
    }

    let optionalErrors;

    if (error && isInvalid) {
      const errorTexts = Array.isArray(error) ? error : [error];
      optionalErrors = errorTexts.map((error, i) => {
        const key = typeof error === 'string' ? error : i;
        return <EuiFormErrorText key={key} id={`${id}-error-${i}`} className="euiFormRow__text">
            {error}
          </EuiFormErrorText>;
      });
    }

    let optionalLabel;
    const isLegend = label && labelType === 'legend' ? true : false;
    const labelId = `${id}-label`;

    if (hasLabel) {
      let labelProps = {};

      if (isLegend) {
        labelProps = {
          type: labelType
        };
      } else {
        labelProps = {
          htmlFor: hasChildLabel ? id : undefined,
          ...(!isDisabled && {
            isFocused: this.state.isFocused
          }),
          // If the row is disabled, don't pass the isFocused state.
          type: labelType
        };
      }

      optionalLabel = <div className="euiFormRow__labelWrapper">
          <EuiFormLabel className="euiFormRow__label" isInvalid={isInvalid} isDisabled={isDisabled} aria-invalid={isInvalid} id={labelId} {...labelProps}>
            {label}
          </EuiFormLabel>
          {labelAppend && ' '}
          {labelAppend}
        </div>;
    }

    const optionalProps = {};
    /**
     * Safe use of ! as default prop is []
     */

    const describingIds = [...describedByIds];

    if (optionalHelpTexts) {
      optionalHelpTexts.forEach(optionalHelpText => describingIds.push(optionalHelpText.props.id));
    }

    if (optionalErrors) {
      optionalErrors.forEach(error => describingIds.push(error.props.id));
    }

    if (describingIds.length > 0) {
      optionalProps['aria-describedby'] = describingIds.join(' ');
    }

    const child = Children.only(children);
    const field = cloneElement(child, {
      id,
      // Allow the child's disabled or isDisabled prop to supercede the `isDisabled`
      disabled: child.props.disabled ?? child.props.isDisabled ?? isDisabled,
      onFocus: this.onFocus,
      onBlur: this.onBlur,
      ...optionalProps
    });
    const fieldWrapperClasses = classNames('euiFormRow__fieldWrapper', {
      euiFormRow__fieldWrapperDisplayOnly:
      /**
       * Safe use of ! as default prop is 'row'
       */
      display.startsWith('center')
    });
    const sharedProps = {
      className: classes,
      id: `${id}-row`
    };
    const contents = <React.Fragment>
        {optionalLabel}
        <div className={fieldWrapperClasses}>
          {field}
          {optionalErrors}
          {optionalHelpTexts}
        </div>
      </React.Fragment>;
    return labelType === 'legend' ? <fieldset {...sharedProps} {...rest}>
        {contents}
      </fieldset> : <div {...sharedProps} {...rest}>
        {contents}
      </div>;
  }

}
EuiFormRow.propTypes = {
  /**
     * Defaults to rendering a `<label>` but if passed `'legend'` for labelType,
     * will render both a `<legend>` and the surrounding container as a `<fieldset>`
     */
  labelType: PropTypes.oneOfType([PropTypes.oneOf(["label"]), PropTypes.oneOf(["legend"])]),
  className: PropTypes.string,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string,

  /**
     * When `rowCompressed`, just tightens up the spacing;
     * Set to `columnCompressed` if compressed
     * and horizontal layout is needed.
     * Set to `center` or `centerCompressed` to align non-input
     * content better with inline rows.
     * Set to `columnCompressedSwitch` if the form control being passed
     * as the child is a switch.
     */

  /**
     * When `rowCompressed`, just tightens up the spacing;
     * Set to `columnCompressed` if compressed
     * and horizontal layout is needed.
     * Set to `center` or `centerCompressed` to align non-input
     * content better with inline rows.
     * Set to `columnCompressedSwitch` if the form control being passed
     * as the child is a switch.
     */
  display: PropTypes.oneOf(["row", "rowCompressed", "columnCompressed", "center", "centerCompressed", "columnCompressedSwitch"]),
  hasEmptyLabelSpace: PropTypes.bool,
  fullWidth: PropTypes.bool,

  /**
     * IDs of additional elements that should be part of children's `aria-describedby`
     */

  /**
     * IDs of additional elements that should be part of children's `aria-describedby`
     */
  describedByIds: PropTypes.arrayOf(PropTypes.string.isRequired),

  /**
     * Escape hatch to not render duplicate labels if the child also renders a label
     */

  /**
     * Escape hatch to not render duplicate labels if the child also renders a label
     */
  hasChildLabel: PropTypes.bool,

  /**
     * ReactElement to render as this component's content
     */

  /**
     * ReactElement to render as this component's content
     */
  children: PropTypes.element.isRequired,
  label: PropTypes.node,

  /**
     * Adds an extra node to the right of the form label without
     * being contained inside the form label. Good for things
     * like documentation links.
     */

  /**
     * Adds an extra node to the right of the form label without
     * being contained inside the form label. Good for things
     * like documentation links.
     */
  labelAppend: PropTypes.any,
  id: PropTypes.string,
  isInvalid: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.node.isRequired, PropTypes.arrayOf(PropTypes.node.isRequired).isRequired]),

  /**
     *  Adds a single node/string or an array of nodes/strings below the input
     */

  /**
     *  Adds a single node/string or an array of nodes/strings below the input
     */
  helpText: PropTypes.oneOfType([PropTypes.node.isRequired, PropTypes.arrayOf(PropTypes.node.isRequired).isRequired]),

  /**
     *  Passed along to the label element; and to the child field element when `disabled` doesn't already exist on the child field element.
     */

  /**
     *  Passed along to the label element; and to the child field element when `disabled` doesn't already exist on the child field element.
     */
  isDisabled: PropTypes.bool
};

try {
  EuiFormRow.__docgenInfo = {
    tags: {},
    filePath: '/app/src/components/form/form_row/form_row.tsx',
    description: '',
    displayName: 'EuiFormRow',
    methods: [],
    props: {
      labelType: {
        defaultValue: {
          value: 'label'
        },
        description: "Defaults to rendering a `<label>` but if passed `'legend'` for labelType,\n" + 'will render both a `<legend>` and the surrounding container as a `<fieldset>`',
        name: 'labelType',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/form/form_row/form_row.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/form/form_row/form_row.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"label" | "legend"',
          value: [{
            value: '"label"'
          }, {
            value: '"legend"'
          }]
        }
      },
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'HTMLAttributes'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'HTMLAttributes'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: 'Defines a string value that labels the current element.\n' + '@see aria-labelledby.',
        name: 'aria-label',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'AriaAttributes'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'AriaAttributes'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      display: {
        defaultValue: {
          value: 'row'
        },
        description: 'When `rowCompressed`, just tightens up the spacing;\n' + 'Set to `columnCompressed` if compressed\n' + 'and horizontal layout is needed.\n' + 'Set to `center` or `centerCompressed` to align non-input\n' + 'content better with inline rows.\n' + 'Set to `columnCompressedSwitch` if the form control being passed\n' + 'as the child is a switch.',
        name: 'display',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/form/form_row/form_row.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/form/form_row/form_row.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"center" | "row" | "rowCompressed" | "columnCompressed" | "centerCompressed" | "columnCompressedSwitch"',
          value: [{
            value: '"center"'
          }, {
            value: '"row"'
          }, {
            value: '"rowCompressed"'
          }, {
            value: '"columnCompressed"'
          }, {
            value: '"centerCompressed"'
          }, {
            value: '"columnCompressedSwitch"'
          }]
        }
      },
      hasEmptyLabelSpace: {
        defaultValue: {
          value: 'false'
        },
        description: '',
        name: 'hasEmptyLabelSpace',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/form/form_row/form_row.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/form/form_row/form_row.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      fullWidth: {
        defaultValue: {
          value: 'false'
        },
        description: '',
        name: 'fullWidth',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/form/form_row/form_row.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/form/form_row/form_row.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      describedByIds: {
        defaultValue: {
          value: '[]'
        },
        description: "IDs of additional elements that should be part of children's `aria-describedby`",
        name: 'describedByIds',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/form/form_row/form_row.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/form/form_row/form_row.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'string[]'
        }
      },
      hasChildLabel: {
        defaultValue: {
          value: 'true'
        },
        description: 'Escape hatch to not render duplicate labels if the child also renders a label',
        name: 'hasChildLabel',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/form/form_row/form_row.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/form/form_row/form_row.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      children: {
        defaultValue: null,
        description: "ReactElement to render as this component's content",
        name: 'children',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/form/form_row/form_row.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'DOMAttributes'
        }, {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/form/form_row/form_row.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'DOMAttributes'
        }, {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'TypeLiteral'
        }],
        required: true,
        type: {
          name: 'ReactElement'
        }
      },
      label: {
        defaultValue: null,
        description: '',
        name: 'label',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/form/form_row/form_row.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/form/form_row/form_row.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'ReactNode'
        }
      },
      labelAppend: {
        defaultValue: null,
        description: 'Adds an extra node to the right of the form label without\n' + 'being contained inside the form label. Good for things\n' + 'like documentation links.',
        name: 'labelAppend',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/form/form_row/form_row.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/form/form_row/form_row.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'any'
        }
      },
      id: {
        defaultValue: null,
        description: '',
        name: 'id',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/form/form_row/form_row.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'HTMLAttributes'
        }, {
          fileName: 'app/src/components/form/form_row/form_row.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'HTMLAttributes'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      isInvalid: {
        defaultValue: null,
        description: '',
        name: 'isInvalid',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/form/form_row/form_row.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/form/form_row/form_row.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      error: {
        defaultValue: null,
        description: '',
        name: 'error',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/form/form_row/form_row.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/form/form_row/form_row.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'ReactNode | ReactNode[]'
        }
      },
      helpText: {
        defaultValue: null,
        description: 'Adds a single node/string or an array of nodes/strings below the input',
        name: 'helpText',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/form/form_row/form_row.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/form/form_row/form_row.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'ReactNode | ReactNode[]'
        }
      },
      isDisabled: {
        defaultValue: null,
        description: "Passed along to the label element; and to the child field element when `disabled` doesn't already exist on the child field element.",
        name: 'isDisabled',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/form/form_row/form_row.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/form/form_row/form_row.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      }
    },
    extendedInterfaces: ['CommonProps', 'HTMLAttributes', 'AriaAttributes', 'DOMAttributes']
  };
} catch (e) {}